
import { computed, inject, nextTick, onBeforeUnmount, onMounted, onUnmounted, ref, shallowRef, watch } from '@vue/composition-api';
import { defineComponent, useContext, useMeta, useRoute, useRouter } from '@nuxtjs/composition-api';
import { declination } from '@artmizu/utils';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { Button, Note, StatusBlock } from '05-ui-kit';
import debounce from 'lodash.debounce';
import CatalogFilter from '~/components/filter/CatalogFilter/CatalogFilter.vue';
import CatalogWideBanner from '~/components/CatalogWideBanner/CatalogWideBanner.vue';
import Breadcrumb from '~/components/Breadcrumb/Breadcrumb.vue';
import TSProductGrid from '~/components/ts-components/TSProductGrid/TSProductGrid.vue';
import FilterButton from '~/components/filter/FilterButton/FilterButton.vue';
import FilterModal from '~/components/filter/FIlterModal/FilterModal.vue';
import SortSelect from '~/components/SortSelect/SortSelect.vue';
import SelectFilter from '~/components/filter/SelectFilter/SelectFilter.vue';
import link from '~/helper/canonical';
import getOpenGraphMarkup from '~/helper/getOpenGraphMarkup';
import type { EmitteryType } from '~/plugins/emitter';
import type Pagination from '~/type/Pagination';
import type { BaseFilter } from '~/type/Filter';
import type Sort from '~/type/Sort';
import ViewportToogle from '~/components/ViewportToogle/ViewportToogle.vue';
import { useResizeStore } from '~/store/resize';
type RequestType = Awaited<ReturnType<typeof context.$api.constructor.catalog>>['result'];
interface ParamsType {
  seo?: string;
  customQuery?: {
    [x: string]: string | (string | null)[];
  };
  page?: number;
  sort?: string | (string | null)[];
  cancel?: boolean;
  availabilitySort?: boolean;
}
interface Sticky {
  destroy: () => void;
}
const __sfc_main = defineComponent({
  head: {}
});
__sfc_main.props = {
  data: {
    key: "data",
    required: true,
    type: null
  },
  list: {
    key: "list",
    required: false,
    type: null,
    default: () => []
  },
  pagination: {
    key: "pagination",
    required: true,
    type: null
  },
  loading: {
    key: "loading",
    required: false,
    type: Boolean,
    default: false
  },
  error: {
    key: "error",
    required: false,
    type: [Boolean, String],
    default: false
  },
  linkPage: {
    key: "linkPage",
    required: true,
    type: String
  },
  sortList: {
    key: "sortList",
    required: true,
    type: Array
  },
  activeSort: {
    key: "activeSort",
    required: true,
    type: null
  },
  availabilitySort: {
    key: "availabilitySort",
    required: false,
    type: Boolean
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const route = useRoute();
  const router = useRouter();
  const context = useContext();
  const activeFilterModal = ref(false);
  let stickySidebar: Sticky | null = null;
  const root = shallowRef<HTMLDivElement>();
  const emitter = inject<EmitteryType>('emitter')!;
  let unsubscribe: () => void;
  const resizeStore = useResizeStore();
  const isBonusesPage = computed(() => {
    return route.value.path === '/catalog/bonuses/';
  });
  const getModelCount = computed(() => {
    return `${props.data.count ? `${props.data.count} ${declination(props.data.count, ['модель', 'модели', 'моделей'])}` : ''}`;
  });
  const debounceChange = debounce(onFilterChange, 300);
  useMeta(() => ({
    title: props.data.meta?.category?.title,
    meta: [{
      hid: 'description',
      name: 'description',
      content: props.data.meta?.category?.description
    }],
    ...getOpenGraphMarkup({
      type: 'product.group',
      image: props.data.thumbnail?.x1 || context.env.projectURL + require('~/assets/images/05-avatar.png'),
      url: context.env.projectURL + route.value.fullPath,
      title: props.data.meta?.category?.title,
      description: props.data.meta?.category?.description
    }),
    ...link({
      exclude: ['filter/'],
      data: {
        route,
        config: context.$config
      }
    })
  }));
  watch(() => route.value.query, () => {
    emit('change-params', {
      page: 1,
      customQuery: route.value.query
    });
    /**
    * Нужно подождать, пока вотчер внутри умного компонента отреагирует на изменения paramsов и сохранит их у себя
    */
    nextTick(() => emit('refresh'));
  });
  onMounted(() => {
    if (!resizeStore.isMobile) initStickySidebar();
    unsubscribe = resizeStore.$onAction(mutation => {
      if (mutation.name === 'setIsMobile') {
        if (mutation.store.isMobile && stickySidebar) {
          stickySidebar.destroy();
          stickySidebar = null;
        } else {
          initStickySidebar();
        }
      }
    });
  });
  onUnmounted(() => {
    if (unsubscribe) unsubscribe();
  });
  onBeforeUnmount(() => {
    stickySidebar?.destroy();
  });
  function initStickySidebar() {
    const {
      default: StickySidebar
    } = require('sticky-sidebar-v2');
    stickySidebar = new StickySidebar('.catalog-page__filter-wrapper', {
      topSpacing: 15,
      bottomSpacing: 15,
      resizeSensor: true,
      minWidth: 280,
      containerSelector: '.catalog-page__filter',
      innerWrapperSelector: '.catalog-page__filter-content'
    });
  }
  function onFilterChange(data: {}, state = false) {
    let customQuery;
    if (Object.keys(data)?.length) {
      customQuery = data;
    } else {
      customQuery = {};
      scrollTop();
    }
    emit('change-params', {
      page: 1,
      customQuery,
      seo: props.data.parentCategoryPath
    });
    /**
    * Нужно подождать, пока вотчер внутри умного компонента отреагирует на изменения paramsов и сохранит их у себя
    */
    nextTick(() => {
      if (resizeStore.resize) {
        if (resizeStore?.resize > 900 || state) emit('change-query');
      }
      emit('refresh');
    });
  }
  function resetFilter() {
    emit('change-params', {
      customQuery: {},
      page: 1
    });
    /**
    * Нужно подождать, пока вотчер внутри умного компонента отреагирует на изменения paramsов и сохранит их у себя
    */
    nextTick(() => {
      emit('change-query', {
        customQuery: {},
        page: 1
      });
      emit('refresh');
      scrollTop();
    });
  }
  function scrollTop() {
    if (root.value) {
      scrollIntoView(root.value, {
        block: 'start'
      });
    }
  }
  function reload() {
    location.reload();
  }

  /**
  * Если значения нет, значит оно было сброшено, поэтому при отсутствии бренда перекидываем на родительскую категорию
  */
  function onBrandFilterChange(data?: BaseFilter) {
    data?.property ? router.push(data.property) : router.push(props.data.parentCategoryPath);
  }

  /**
  * Если значения нет, значит оно было сброшено, поэтому при отсутствии модели перекидываем на родительский бренд этой модели
  */
  function onModelFilterChange(data?: BaseFilter) {
    if (data?.property) router.push(data.property);else if (props.data.filter?.brand?.value) router.push(props.data.filter.brand?.value?.property);
  }
  function onAvailabilityChange(event: boolean, sort: string) {
    emit('change-params', {
      availabilitySort: event,
      sort
    });
    nextTick(() => {
      emit('change-query');
      emit('refresh');
    });
  }
  return {
    emit,
    activeFilterModal,
    root,
    emitter,
    isBonusesPage,
    getModelCount,
    debounceChange,
    onFilterChange,
    resetFilter,
    scrollTop,
    reload,
    onBrandFilterChange,
    onModelFilterChange,
    onAvailabilityChange
  };
};
__sfc_main.components = Object.assign({
  CatalogFilter,
  FilterModal,
  CatalogWideBanner,
  Breadcrumb,
  ViewportToogle,
  StatusBlock,
  Button,
  TSProductGrid,
  Note,
  FilterButton,
  SortSelect,
  SelectFilter
}, __sfc_main.components);
export default __sfc_main;
